/* eslint-disable no-param-reassign */
/* eslint-disable no-console */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import jwtDecode from 'jwt-decode';
import {
  getToken,
  clearAllAuthData,
  isTokenValid,
  decodeToken,
  refreshToken as refreshTokenUtil,
  updateLastActivity,
} from '../../utils/auth/tokenManager';
import { clearBrandMarketplaceCache } from '../../utils/marketplace/brandMarketplaceCache';
import { clearInfluencerMarketplaceCache } from '../../utils/marketplace/influencerMarketplaceCache';
import { resetBrandWorkQueueCache } from '../../hooks/collaborations/useBrandCollaborationWorkQueue';
import { resetInfluencerCollaborationsCache } from '../../hooks/collaborations/useInfluencerCollaborations';
import { resetAcceptanceDetailsCache } from '../../hooks/collaborations/useAcceptanceDetailsFetch';
import { resetFollowedBrandsCache } from '../../hooks/influencer/useFollowedBrands';
import { resetFavoritedCampaignsCache } from '../../hooks/influencer/useFavoritedCampaigns';
import { resetInfluencerMetricsCache } from '../../hooks/influencer/useInfluencerMetrics';
import { resetInfluencerProfileCache } from '../../hooks/influencer/useInfluencerProfile';
import { resetViewHistoryCache } from '../../hooks/influencer/useViewHistory';
import { resetProfileSummaryCache } from '../../utils/shared/profileSummaryCache';
import { resetRecentWizardCheck } from '../../utils/shared/wizardCheckSession';

export const refreshTokenThunk = createAsyncThunk(
  'auth/refreshToken',
  async (apiUrl, { rejectWithValue }) => {
    try {
      const newToken = await refreshTokenUtil(apiUrl);
      if (!newToken) {
        return rejectWithValue('Token refresh failed');
      }

      const decoded = decodeToken(newToken);
      return {
        token: newToken,
        userType: decoded?.user_type || null,
        subscriptionPlan: decoded?.subscription_plan || 'Free',
      };
    } catch (error) {
      console.error('Token refresh thunk error:', error);
      return rejectWithValue(error.message);
    }
  },
);

export const validateTokenThunk = createAsyncThunk(
  'auth/validateToken',
  async (_, { rejectWithValue }) => {
    try {
      const token = getToken();

      if (!token || !isTokenValid(token)) {
        return rejectWithValue('Token is invalid or expired');
      }

      const decoded = decodeToken(token);
      updateLastActivity();

      return {
        userType: decoded?.user_type || null,
        subscriptionPlan: decoded?.subscription_plan || 'Free',
      };
    } catch (error) {
      console.error('Token validation error:', error);
      return rejectWithValue(error.message);
    }
  },
);

const initializeState = () => {
  const defaultState = {
    isAuthenticated: false,
    userType: null,
    subscriptionPlan: 'Free',
    profileImageUrl: null,
    isRefreshing: false,
    refreshError: null,
    lastValidated: null,
    permissions: [],
  };

  const token = getToken();
  if (!token) {
    return defaultState;
  }

  try {
    const decoded = jwtDecode(token);
    const currentTime = Date.now() / 1000;

    if (decoded.exp && decoded.exp > currentTime) {
      return {
        ...defaultState,
        isAuthenticated: true,
        userType: decoded.user_type,
        subscriptionPlan: decoded.subscription_plan || 'Free',
        lastValidated: Date.now(),
        permissions: decoded.permissions || [],
      };
    }

    clearAllAuthData();
    return defaultState;
  } catch (error) {
    console.error('Token decode error:', error);
    clearAllAuthData();
    return defaultState;
  }
};

const authSlice = createSlice({
  name: 'auth',
  initialState: initializeState(),
  reducers: {
    login: (state, action) => {
      state.isAuthenticated = true;
      state.lastValidated = Date.now();
      state.refreshError = null;

      if (action.payload && action.payload.userType && typeof action.payload.userType === 'string' && action.payload.userType.length > 0) {
        state.userType = action.payload.userType;
        state.subscriptionPlan = action.payload.subscriptionPlan || 'Free';
        state.profileImageUrl = action.payload.profileImageUrl || null;
        state.permissions = action.payload.permissions || [];
      } else {
        state.userType = null;
        state.subscriptionPlan = 'Free';
        state.profileImageUrl = null;
        state.permissions = [];
      }

      updateLastActivity();
    },

    updateSubscription: (state, action) => {
      state.subscriptionPlan = action.payload;
    },

    logout: (state) => {
      state.isAuthenticated = false;
      state.userType = null;
      state.subscriptionPlan = 'Free';
      state.profileImageUrl = null;
      state.isRefreshing = false;
      state.refreshError = null;
      state.lastValidated = null;
      state.permissions = [];

      clearAllAuthData();
      clearBrandMarketplaceCache();
      clearInfluencerMarketplaceCache();
      resetBrandWorkQueueCache();
      resetInfluencerCollaborationsCache();
      resetAcceptanceDetailsCache();
      resetFollowedBrandsCache();
      resetFavoritedCampaignsCache();
      resetInfluencerMetricsCache();
      resetInfluencerProfileCache();
      resetViewHistoryCache();
      resetProfileSummaryCache();
      resetRecentWizardCheck();
      // Also directly remove token for test compatibility
      try {
        localStorage.removeItem('token');
      } catch (error) {
        // Ignore errors in test environment
      }
    },

    updateProfileImage: (state, action) => {
      state.profileImageUrl = action.payload;
    },

    updatePermissions: (state, action) => {
      state.permissions = action.payload || [];
    },

    syncFromToken: (state, action) => {
      const { userType, subscriptionPlan, permissions } = action.payload;
      state.userType = userType;
      state.subscriptionPlan = subscriptionPlan || state.subscriptionPlan;
      state.permissions = permissions || state.permissions;
      state.lastValidated = Date.now();
    },

    clearRefreshError: (state) => {
      state.refreshError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(refreshTokenThunk.pending, (state) => {
        state.isRefreshing = true;
        state.refreshError = null;
      })
      .addCase(refreshTokenThunk.fulfilled, (state, action) => {
        state.isRefreshing = false;
        state.refreshError = null;
        state.lastValidated = Date.now();
        state.userType = action.payload.userType;
        state.subscriptionPlan = action.payload.subscriptionPlan;
      })
      .addCase(refreshTokenThunk.rejected, (state, action) => {
        state.isRefreshing = false;
        state.refreshError = action.payload || 'Token refresh failed';
        console.error('Token refresh failed');
      })
      .addCase(validateTokenThunk.pending, () => {
        // Don't set loading state for validation - intentionally empty
      })
      .addCase(validateTokenThunk.fulfilled, (state, action) => {
        state.isAuthenticated = true;
        state.lastValidated = Date.now();
        state.userType = action.payload.userType;
        state.subscriptionPlan = action.payload.subscriptionPlan;
      })
      .addCase(validateTokenThunk.rejected, (state) => {
        state.isAuthenticated = false;
        state.userType = null;
        state.subscriptionPlan = 'Free';
        state.lastValidated = null;
      });
  },
});

export const {
  login,
  logout,
  updateSubscription,
  updateProfileImage,
  updatePermissions,
  syncFromToken,
  clearRefreshError,
} = authSlice.actions;

export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectUserType = (state) => state.auth.userType;
export const selectSubscriptionPlan = (state) => state.auth.subscriptionPlan;
export const selectIsRefreshing = (state) => state.auth.isRefreshing;
export const selectRefreshError = (state) => state.auth.refreshError;
export const selectPermissions = (state) => state.auth.permissions;
export const selectLastValidated = (state) => state.auth.lastValidated;

// Helper selector to check if user has a specific permission
export const selectHasPermission = (permission) => (state) => {
  const permissions = state.auth.permissions || [];
  return permissions.includes(permission);
};

// Helper selector to check if user has all required permissions
export const selectHasAllPermissions = (requiredPermissions) => (state) => {
  const permissions = state.auth.permissions || [];
  return requiredPermissions.every((p) => permissions.includes(p));
};

// Helper selector to check if user has any of the required permissions
export const selectHasAnyPermission = (requiredPermissions) => (state) => {
  const permissions = state.auth.permissions || [];
  return requiredPermissions.some((p) => permissions.includes(p));
};

export default authSlice.reducer;
